import {
  initialize as initializeLD,
  type LDClient,
  type LDContext,
} from 'launchdarkly-js-client-sdk'

import { type User } from '@webapp/models'
import { Teams } from './enums'
import {
  type FeatureFlagConfig,
  type FeatureFlagKey,
  type FeatureFlags,
} from './types'

export const FEATURE_FLAGS_CONFIG: FeatureFlagConfig = {
  // GRANDFATHERED
  CHQ_34_ADD_AMZNPO_TAX_SHIP: [false, Teams.GRANDFATHERED],
  SY_1803_PUNCHOUT_ON_BEHALF_OF: [false, Teams.GRANDFATHERED],
  FT_4880_ENABLE_INTERCOM: [false, Teams.GRANDFATHERED],
  FT_4280_BILL_PAYMENT_CONSOLIDATION: [false, Teams.GRANDFATHERED],

  // Team AP
  FT_7515_FIX_CREATE_BILL_PERMISSION_ACCESS: [false, Teams.TEAM_AP],
  FT_9948_INITIALIZE_DRAFT_BILL_WITH_PO: [false, Teams.TEAM_AP],
  AP_10029_UNBILLED_ITEMS_PAGE_MIGRATION: [false, Teams.TEAM_AP],
  PAY_2953_IMPROVE_BILL_FILTERING: [false, Teams.TEAM_AP],
  AP_10171_AP_INLINE_EDITS: [false, Teams.TEAM_AP],
  AP_10285_EDITABLE_TAX_AMOUNT: [false, Teams.TEAM_AP],

  // Team CLC
  CL_1058_AP_GRANULAR_PERMISSIONS: [false, Teams.TEAM_CLC],
  CL_1324_NEW_NO_PERMISSION_PAGE: [false, Teams.TEAM_CLC],

  // Team Pay
  PAY_2700_INTERNATIONAL_PAYMENTS: [false, Teams.TEAM_PAYMENTS],
  PAY_3401_ENABLE_AWX_MIGRATION_BANNER: [false, Teams.TEAM_PAYMENTS],
  PAY_2776_AUTOMATED_REMITTANCE_EMAIL: [false, Teams.TEAM_PAYMENTS],
  PAY_24_CANADIAN_SPENDING_CARDS: [false, Teams.TEAM_PAYMENTS],
  PAY_3370_BULK_APPROVE_PAYMENTS: [false, Teams.TEAM_PAYMENTS],
  PAY_3569_BULK_PREPARE_PAYMENT: [false, Teams.TEAM_PAYMENTS],
  PAY_3736_DISABLE_STRIPE_SPEND_CARD: [false, Teams.TEAM_PAYMENTS],

  // Team Integrations
  INT_1976_MIGRATE_MANAGE_USERS_AND_PERMISSIONS: [
    false,
    Teams.TEAM_INTEGRATIONS,
  ],

  // Team Platform
  PLAT_2155_MERGE_NETSUITE: [false, Teams.TEAM_PLATFORM],
  PLAT_3572_PRO_PERMISSIONS_ROLES: [false, Teams.TEAM_PLATFORM],
  PLAT_3765_SLACK_INTEGRATION_MIGRATION: [false, Teams.TEAM_PLATFORM],
  PLAT_3964_COMMENT_MENTION_REGEX_FIX: [false, Teams.TEAM_PLATFORM],
  PLAT_3968_MARK_BILL_AS_SYNCED: [false, Teams.TEAM_PLATFORM],
  PLAT_4175_CC_AND_EXPENSE_BILL_STATUS_WORKATO: [false, Teams.TEAM_PLATFORM],
  PLAT_4291_OVERRIDE_NETSUITE_TAX_AMOUNT: [false, Teams.TEAM_PLATFORM],

  // Team Purchasing
  CT_700_CREATE_PO_SHIPPING_ADDRESS_USE_REQUESTER_NAME: [
    false,
    Teams.TEAM_PURCHASING,
  ],
  RFO_AUTOMATION: [false, Teams.TEAM_PURCHASING],
  FT_5817_ENABLE_BUDGETS_REDESIGN: [false, Teams.TEAM_PURCHASING],
  FT_5815_ENABLE_RECEIVED_CATEGORY: [false, Teams.TEAM_PURCHASING],
  PUR_2836_ORDER_LIFECYCLE_TRACKER: [false, Teams.TEAM_PURCHASING],
  PUR_3093_DENIAL_CONFIRMATION: [false, Teams.TEAM_PURCHASING],
  PUR_3039_ORDER_STATUS_TRACKER_UPCOMING_APPROVALS: [
    false,
    Teams.TEAM_PURCHASING,
  ],
  PUR_3180_NEXT_GEN_INTAKE_REDESIGN: [false, Teams.TEAM_PURCHASING],

  // Team Data
  FT_2274_ENABLE_SPEND_INSIGHTS_DASHBOARD: [false, Teams.TEAM_DATA],
  DATA_380_ENABLE_SPEND_ANALYTICS_SALES_DEMO: [false, Teams.TEAM_DATA],
  DATA_ENABLE_SPEND_INSIGHTS_BETA_FEATURES: [false, Teams.TEAM_DATA],
  DATA_1808_ENABLE_THOUGHTSPOT_BUDGET: [false, Teams.TEAM_DATA],
  DATA_1439_GLOBAL_SEARCH: [false, Teams.TEAM_DATA],

  // Team Architecture
  SEGMENT_USE_UNIQUE_ID: [false, Teams.TEAM_ARCHITECTURE],
  ARCH_468_MIGRATION_SETTINGS_UNIT: [false, Teams.TEAM_ARCHITECTURE],
  ARCH_514_MIGRATION_EXPENSE_APPROVALS: [false, Teams.TEAM_ARCHITECTURE],
  ARCH_531_MIGRATION_EXPENSE_DETAILS: [false, Teams.TEAM_ARCHITECTURE],
  ARCH_544_MIGRATION_EXPENSE_REQUEST: [false, Teams.TEAM_ARCHITECTURE],
  // Team Design System
}

export const FEATURE_FLAG_DEFAULTS = Object.keys(FEATURE_FLAGS_CONFIG).reduce(
  (obj, key) => {
    const [defaultValue] = FEATURE_FLAGS_CONFIG[key as FeatureFlagKey]

    return {
      [key]: defaultValue,
      ...obj,
    }
  },
  {}
) as FeatureFlags

interface FeatureFlagServiceConfig {
  clientId: string
  user: User
  domain: string
}

export class FeatureFlagService {
  private static instance: FeatureFlagService | null = null
  private client: LDClient
  private clientId: string
  private options: LDContext
  initialized: boolean = false

  private constructor(config: FeatureFlagServiceConfig) {
    const { clientId, user, domain } = config

    this.clientId = clientId

    this.options = {
      key: 'aa0ceb',
      firstName: user?.firstName,
      lastName: user?.lastName,
      email: user?.email,
      custom: {
        domain,
      },
    }

    this.client = initializeLD(this.clientId, this.options, {
      sendEventsOnlyForVariation: true,
      streaming: true,
    })
  }

  public static async initialize(
    config: FeatureFlagServiceConfig
  ): Promise<FeatureFlagService> {
    if (!FeatureFlagService.instance) {
      FeatureFlagService.instance = new FeatureFlagService(config)
      await FeatureFlagService.instance.initializeClient()
    }
    return FeatureFlagService.instance
  }

  public static getInstance(): FeatureFlagService {
    if (!FeatureFlagService.instance) {
      throw new Error('FeatureFlagService has not been initialized')
    }
    return FeatureFlagService.instance
  }

  private initializeClient(): Promise<void> {
    return new Promise((resolve, reject) => {
      this.client.on('ready', () => {
        this.initialized = true
        resolve()
      })
      this.client.on('failed', (error) => {
        this.initialized = false
        reject(new Error(`LD Client failed to initialize: ${error}`))
      })
    })
  }

  public getFlag(flagKey: string) {
    if (!this.initialized) {
      throw new Error('getFlags method: FeatureFlagService is not initialized')
    }

    return this.client.variation(flagKey)
  }

  public getAllFlags() {
    if (!this.initialized) {
      throw new Error(
        'getAllFlags method: FeatureFlagService is not initialized'
      )
    }
    return this.client.allFlags()
  }
}
